import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { MdCheckCircle, MdContentCopy } from 'react-icons/md';
import { usePixPayment } from '@/hooks/usePixPayment';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import NeonButton from '@/components/NeonButton';

const planOptions = [
  {
    id: 'premium',
    name: 'Premium',
    price: 'R$ 49,90',
    description: 'Acesso completo e prioritário a todos recursos',
    period: '30 dias',
    isPopular: true,
    benefits: [
      'Todas as ligas (com campeonatos exclusivos)',
      'Jogos ao vivo e alertas push',
      'Placar ao vivo e estatísticas detalhadas',
      'Gráficos de pressão, histórico e tendências',
      'Tendências estatísticas exclusivas',
      'Suporte prioritário 24/7',
    ],
  },
];

const Timeline = ({ step }: { step: number }) => (
  <Box position="relative" width="100%" maxWidth="380px" mx="auto" mb={5}>
    <Box
      sx={{
        position: 'absolute',
        top: '26px',
        left: 0,
        right: 0,
        height: '4.2px',
        zIndex: 0,
        background: 'linear-gradient(90deg, #34fad2 0%, #358d6d 100%)',
        boxShadow: '0 0 18px #2af49799',
        borderRadius: 5,
      }}
    />
    <Box display="flex" justifyContent="space-between" alignItems="center" position="relative" zIndex={2}>
      {[1, 2, 3].map((n, idx) => (
        <Box key={n} display="flex" flexDirection="column" alignItems="center" width="33.33%">
          <Box sx={{
            width: 52, height: 52, mb: .4,
            borderRadius: '50%',
            boxShadow: step === idx ? '0 0 18px 6px #2af497af' : step > idx ? '0 0 18px 3px #2af49766' : '0 1.5px 5px #0a281a',
            background: step > idx ? 'radial-gradient(ellipse at 60% 20%,#2af497,#146450 90%)' : step === idx ? 'linear-gradient(149deg,#27f5b9 70%, #111919 130%)' : '#191919',
            border: step >= idx ? '2.8px solid #2af497' : '2px solid #222',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .3s',
          }}>
            {step > idx ? <MdCheckCircle size={25} color='#fff' /> : <Typography fontSize={22} fontWeight={700} color={step >= idx ? '#222' : '#bbb'}>{n}</Typography>}
          </Box>
          <Typography variant="body1" fontWeight={step === idx ? 800 : 400} sx={{ color: step === idx ? '#2af497' : '#aaa', letterSpacing: .2, fontSize: 14.5 }}>{
            idx === 0 ? 'Escolher Plano' : idx === 1 ? 'Pagar Pix' : 'Confirmação'
          }</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default function Checkout() {
  const [selectedPlan, setSelectedPlan] = useState<typeof planOptions[0] | null>(planOptions[0]);
  const [step, setStep] = useState(0);
  const [copySuccess, setCopySuccess] = useState('');
  const [loadingLogout, setLoadingLogout] = useState(false);
  const navigate = useNavigate();
  const {
    loading,
    paymentStatus,
    qrCodeText,
    createPixPayment,
    resetPayment,
    checkPaymentStatus,
    currentTransaction,
  } = usePixPayment();

  useEffect(() => {
    let interval: any = undefined;
    if (paymentStatus === 'pending' && currentTransaction) {
      interval = setInterval(() => {
        checkPaymentStatus(currentTransaction);
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [paymentStatus, currentTransaction, checkPaymentStatus]);

  useEffect(() => {
    if (paymentStatus === 'completed') setStep(2);
  }, [paymentStatus]);

  const payPix = async () => {
    if (!selectedPlan) return;
    await createPixPayment(49.90, `Assinatura ${selectedPlan.name} - Precision`);
    setStep(1);
  };

  const handleCopy = async () => {
    if (qrCodeText) {
      await navigator.clipboard.writeText(qrCodeText);
      setCopySuccess('Copiado!');
      setTimeout(() => setCopySuccess(''), 1200);
    }
  };

  const logoutPix = () => {
    setLoadingLogout(true);
    localStorage.clear();
    setTimeout(() => {
      window.location.href = '/login';
    }, 700);
  };

  return (
    <Box minHeight="100vh" position="relative" px={2} py={6}
      sx={{
        background: '#000',
        minHeight: '100vh',
        overflowX: 'hidden',
        fontFamily: 'Inter, Arial, sans-serif',
      }}>
      <Box
        mx="auto" borderRadius={8} p={{ xs: 2, sm: 6 }} pb={5}
        sx={{
          width: { xs: '95%', md: '80%' },
          bgcolor: 'rgb(36,44,53)',
          border: '3.3px solid #1ea982',
          position: 'relative',
          backdropFilter: 'blur(4.5px)',
          pt: 5, pb: 4,
          background: 'linear-gradient(rgba(0, 0, 0, 0.81), rgba(0, 0, 0, 0.76)), url("/src/assets/low-poly-grid.png") 50% center / cover no-repeat lightgray',
        }}>
        <Typography variant="h4" fontWeight={900} textAlign="center" color="#2af497" mb={2}
          sx={{ textShadow:'0 3px 28px #16eb7f55', letterSpacing:'.7px', fontFamily:'UnifrakturCook, Inter, Arial'}}>
          Nossos Planos
        </Typography>
        <Timeline step={step} />

        {step === 0 && (
          <Box>
            <Grid container spacing={7} mb={7}>
              {planOptions.map((plan) => (
                <Grid item xs={12} sm={12} key={plan.id} display='flex' justifyContent='center'>
                  <Card
                    sx={{
                      background: plan.id === 'premium' ? 'rgba(0,0,0,0.38)' : 'rgb(36,44,53)',
                      border: plan.isPopular ? '2.8px solid #2af497' : (plan.id === 'premium' ? '1.5px solid rgba(255,255,255,0.08)' : '2.2px solid #34382f'),
                      boxShadow: '0 6px 36px 0 rgba(0,0,0,0.13)',
                      borderRadius: 12,
                      width: '100%',
                      minHeight: 340,
                      p: 2.5,
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transform: selectedPlan?.id === plan.id ? 'scale(1.03) translateY(-5px)' : 'scale(1)',
                      zIndex: plan.isPopular || plan.id === selectedPlan?.id ? 5 : 1,
                      backdropFilter: plan.id === 'premium' ? 'blur(12px) saturate(140%)' : 'blur(9px) brightness(1.08)',
                    }}
                    onClick={() => setSelectedPlan(plan)}>
                    <CardContent>
                      {plan.isPopular && <Chip label="Popular" color="success" sx={{ position: 'absolute', top: 21, right: 18, fontWeight: 900, fontSize: 17, letterSpacing: .2, borderRadius: 2 }} />}
                      <Typography variant="h6" fontWeight={900} color="#2af497" mb={.7} sx={{ textShadow:'0 2.5px 11px #15f77615', fontSize: 26, fontFamily: 'UnifrakturCook' }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="h3" color="#2af497" mb={1.2}
                        fontWeight={900} sx={{ letterSpacing: 1.4, lineHeight: 1, textShadow: '0 2px 24px #22ef8828', fontSize: { xs: 31, sm: 38 }, fontFamily: 'UnifrakturCook' }}>{plan.price}</Typography>
                      <Typography variant="body1" color="#f4fefb" mb={1.1} fontSize={18} fontWeight={400} fontFamily='Inter'>{plan.description}</Typography>
                      <Typography variant="body2" color="#e6ece8" mb={2}>
                        Período: <span style={{ color: '#2af497', fontWeight: 700 }}>{plan.period}</span>
                      </Typography>
                      <Box component="ul" sx={{
                        pl: 2,
                        color: '#baffda',
                        fontFamily: 'Inter',
                        fontSize: 16,
                        fontWeight: 500,
                        mb: .3,
                        listStyleType: 'disc',
                        lineHeight: 2.0,
                        '&>li': {
                          marginBottom: '2px',
                          ml: 1,
                          fontWeight: 600,
                          letterSpacing: '.2px',
                        },
                        '& li::marker': { color: '#2af497', fontSize: '20px' }
                      }}>
                        {plan.benefits.map((benefit, idx) => (
                          <li key={idx}>{benefit}</li>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box mt={3}>
              <NeonButton
                fullWidth
                sx={{
                  width: '100%',
                  py: 2.3,
                  fontSize: 22,
                  fontWeight: 900,
                  mt: 2,
                  borderRadius: 3,
                  background: '#2af497',
                  color: '#12191a',
                  borderColor: '#2af497'
                }}
                disabled={!selectedPlan || loading}
                onClick={payPix}
              >
                {loading && <CircularProgress color="inherit" size={33} style={{ marginRight: 11, verticalAlign: 'middle' }} />}
                {loading ? 'Gerando PIX...' : 'Pagar PIX'}
              </NeonButton>
            </Box>
          </Box>
        )}

        {/* QR Code e Pagamento */}
        {step === 1 && (
          <Box textAlign="center" pt={1}>
            <Typography variant="h6" color="#e1fff0" mb={2.2} fontWeight={700} fontFamily='Inter'>
              Abra o app bancário, escaneie o QR ou copie o código:
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" flexDirection="column" alignItems="center" gap={3} position="relative">
              {loading && (
                <Box position="absolute" top={-25} left={0} right={0} zIndex={15} display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress color="success" size={73} sx={{ my: 3, background: '#131', borderRadius: '50%', px: 5, boxShadow: '0 0px 26px #2af4972d' }} />
                </Box>
              )}
              <Box bgcolor="#fff" borderRadius={6} p={2} display="inline-block" mx="auto" style={{ maxWidth: 255 }}>
                {qrCodeText && (
                  <QRCode value={qrCodeText} size={205} />
                )}
              </Box>
              <Card sx={{ p: 1.2, px: { xs: 1.2, sm: 2.8 }, bgcolor: '#21261f', color: '#2af497', border: '2.2px solid #1ffdb2', borderRadius: 6, wordBreak: 'break-all', maxWidth: 345, width: '99vw', minWidth: '100px', overflow: 'auto', fontSize: 17, fontFamily: 'monospace', textAlign: 'left', boxShadow: 'none' }}>
                <Box display="flex" alignItems="center">
                  <span style={{ flex: 1, overflow: 'auto', fontFamily: 'monospace', fontSize: 15, display: 'block', wordBreak: 'break-all', whiteSpace: 'pre-line', maxWidth: '290px' }}>{qrCodeText}</span>
                  <IconButton size="small" sx={{ ml: 1, color: '#2af497' }} onClick={handleCopy}><MdContentCopy /></IconButton>
                  <span style={{ marginLeft: 8, fontSize: 15, color: '#2af497' }}>{copySuccess}</span>
                </Box>
              </Card>
              <Alert severity="info" sx={{ mt: 1.3, fontSize: 17, color: '#2af497', bgcolor: '#18211a' }}>Após pagar no banco, clique no botão abaixo.</Alert>
              <Box mt={2} width="100%">
                <NeonButton
                  fullWidth
                  sx={{
                    width: '100%',
                    py: 2.3,
                    fontSize: 22,
                    fontWeight: 900,
                    mt: 1,
                    borderRadius: 3,
                    background: '#2af497',
                    color: '#12191a',
                    borderColor: '#2af497'
                  }}
                  disabled={loadingLogout}
                  onClick={logoutPix}
                >
                  {loadingLogout && <CircularProgress color="inherit" size={30} style={{ marginRight: 13, verticalAlign: 'middle' }} />}
                  {loadingLogout ? 'Saindo...' : 'JÁ PAGUEI O PIX'}
                </NeonButton>
              </Box>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box textAlign="center">
            <Alert severity="success" sx={{ fontSize: 23, mb: 3, color: '#22bb77', background: '#194e3b' }}>
              Pagamento confirmado com sucesso! <br /> Bem-vindo ao Precision!
            </Alert>
            <Box mt={2} width="100%">
              <NeonButton fullWidth sx={{ py: 2.2, fontWeight: 800, borderRadius: 3, fontSize: 21 }} onClick={() => navigate('/')}>Ir para Home</NeonButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
